import { Backdrop } from '@mui/material';
import UPLoadingSpinner from 'components/loading-spinner';
import { useCallback, useState } from 'react';
import { GENERAL__CANCEL_BUTTON } from 'translations/constants';
import IToken from 'types/auth/IToken';
import MfaBackupCodesView from './MfaBackupCodesView';
import MfaSetupView from './MfaSetupView';
import { MfaEnrollmentModalContainer, MfaEnrollmentModalStyled, MfaModalCard } from './styles';
import useMfaEnrollment from './useMfaEnrollment';

type MfaStep = 'setup' | 'codes';

interface MfaEnrollmentModalProps {
  /** Fresh pending-enrollment token from `POST /Auth/Mfa/OptIn`. */
  mfaToken: string;
  /** The user backed out. Nothing is persisted, so the banner stays and they can start again. */
  onCancel: () => void;
  /** A failure the flow cannot recover from here, carrying the backend's `[MFA] …` key to display. */
  onFailed: (messageKey: string) => void;
  /** Enrollment committed. The session in `token` is the one that reflects it — store it. */
  onEnrolled: (session: IToken) => void;
}

/**
 * The grace-period opt-in flow: the same two screens as first-time enrollment, in a dialog over the
 * page the user was already on.
 *
 * A dialog rather than the `/auth/mfa-enrollment` route because opting in is not a login. The route
 * renders the sign-in page's full-bleed background and is only reachable while signed *out*
 * (`Router` swaps to `AppRouter` the moment a token exists), and the story requires the user to land
 * back where they were — which costs nothing if they never left.
 *
 * The mfaToken lives in the parent's state only: it is not a session token and must never reach
 * Redux, localStorage or a URL. Reloading mid-flow therefore loses it, which is correct — nothing has
 * been persisted, so the banner comes back and mints a new one.
 */
const MfaEnrollmentModal = (props: MfaEnrollmentModalProps) => {
  const { mfaToken, onCancel, onFailed, onEnrolled } = props;
  const [step, setStep] = useState<MfaStep>('setup');
  const [session, setSession] = useState<IToken>(null);

  const { payload, loading, submitting, failure, retrySeconds, confirm, clearFailure } = useMfaEnrollment(
    mfaToken,
    onFailed,
  );

  const onSubmitCode = useCallback(
    async (code: string) => {
      const token = await confirm(code);
      if (token) {
        setSession(token);
        setStep('codes');
      }
    },
    [confirm],
  );

  const onConfirmCodesStored = useCallback(() => onEnrolled(session), [onEnrolled, session]);

  // Backdrop clicks are ignored on both steps, and the codes step is not dismissible at all: by then
  // the enrollment is already committed and these codes can never be shown again. Cancel on the setup
  // step is the deliberate way out, and the banner itself stays dismissible — the story is explicit
  // that enrollment must remain optional during the grace period.
  const onModalClose = useCallback(
    (_event: object, reason: string) => {
      if (step === 'setup' && reason === 'escapeKeyDown') {
        onCancel();
      }
    },
    [step, onCancel],
  );

  return (
    <Backdrop open sx={{ zIndex: 9400 }}>
      <MfaEnrollmentModalStyled open onClose={onModalClose}>
        <MfaEnrollmentModalContainer>
          <MfaModalCard>
            {loading && <UPLoadingSpinner />}
            {!loading && payload && step === 'setup' && (
              <MfaSetupView
                payload={payload}
                submitting={submitting}
                failure={failure}
                retrySeconds={retrySeconds}
                onSubmit={onSubmitCode}
                onCancel={onCancel}
                cancelLabelKey={GENERAL__CANCEL_BUTTON}
                onCodeEdited={clearFailure}
              />
            )}
            {!loading && payload && step === 'codes' && (
              <MfaBackupCodesView codes={payload.backupCodes} onConfirm={onConfirmCodesStored} />
            )}
          </MfaModalCard>
        </MfaEnrollmentModalContainer>
      </MfaEnrollmentModalStyled>
    </Backdrop>
  );
};

export default MfaEnrollmentModal;
